import { useState } from 'react';
import { KeyRound, Eye, EyeOff, Lock } from 'lucide-react';
import Modal from '../../../../components/modals/Modal';
import Button from '../../../../components/ui/Button';
import { useAuth } from '../../../../hooks/useAuth';

export default function ChangePasswordModal({ isOpen, onClose }) {
  const { changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validate = () => {
    if (!currentPassword) return 'Please enter your current password.';
    if (!newPassword) return 'Please enter a new password.';
    if (newPassword.length < 6) return 'New password must be at least 6 characters.';
    if (newPassword !== confirmPassword) return 'Passwords do not match.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setLoading(true);
    try {
      await changePassword({ oldPassword: currentPassword, newPassword });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (apiErr) {
      setError(apiErr?.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess(false);
  };

  const inputClasses =
    'block w-full rounded-lg border border-brand-gray bg-brand-gray-light text-[15px] text-brand-navy placeholder-brand-navy/30 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold focus:bg-white transition-colors px-4 py-3 pr-10';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Change Password" icon={KeyRound} maxWidth="max-w-md">
      {success ? (
        <div className="text-center py-4">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-green-600" />
          </div>
          <h4 className="text-lg font-bold text-brand-navy mb-1">Password Updated</h4>
          <p className="text-[15px] text-brand-navy/60 mb-6">Your password has been changed successfully.</p>
          <Button onClick={handleClose} variant="primary" className="w-full">
            Done
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-[15px] text-red-700">{error}</div>
          )}

          <div>
            <label className="block text-[15px] font-semibold text-brand-navy mb-2">Current Password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={inputClasses}
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/40 hover:text-brand-navy"
                tabIndex={-1}
                aria-label="Toggle current password"
              >
                {showCurrent ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[15px] font-semibold text-brand-navy mb-2">New Password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={inputClasses}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/40 hover:text-brand-navy"
                tabIndex={-1}
                aria-label="Toggle new password"
              >
                {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-brand-navy/40">Minimum 6 characters</p>
          </div>

          <div>
            <label className="block text-[15px] font-semibold text-brand-navy mb-2">Confirm New Password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputClasses}
                placeholder="Re-enter new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/40 hover:text-brand-navy"
                tabIndex={-1}
                aria-label="Toggle confirm password"
              >
                {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex space-x-3 pt-2">
            <Button type="button" variant="secondary" className="flex-1" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" className="flex-1" loading={loading}>
              Update Password
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
